import { useState, useMemo } from 'react';
import {
  Search, AlertTriangle, CheckCircle2, XCircle, Clock, Wifi, WifiOff, HelpCircle,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { AdminTabShell } from './AdminTabShell';
import {
  type TelemetryHealthRow,
  type TelemetryHealthData,
} from '@/hooks/admin/useAdminTelemetryHealth';
import type { TelemetryHealthStatus } from '@/lib/telemetry-health';

// ─── Props ──────────────────────────────────────────────────────────────

interface TelemetryHealthTabProps {
  data: TelemetryHealthData | null;
  isLoading: boolean;
  error: Error | null;
}

// ─── Status display config ──────────────────────────────────────────────

const STATUS_LABELS: Record<TelemetryHealthStatus, string> = {
  healthy: 'Healthy',
  stale: 'Stale',
  no_recent_data: 'No Recent Data',
  never_reported: 'Never Reported',
  provider_error: 'Provider Error',
  zero_data_only: 'Zero Data Only',
  unknown: 'Unknown',
};

const STATUS_COLORS: Record<TelemetryHealthStatus, string> = {
  healthy: 'bg-emerald-900/50 text-emerald-300',
  stale: 'bg-amber-900/50 text-amber-300',
  no_recent_data: 'bg-orange-900/50 text-orange-300',
  never_reported: 'bg-red-900/50 text-red-300',
  provider_error: 'bg-red-900/50 text-red-300',
  zero_data_only: 'bg-purple-900/50 text-purple-300',
  unknown: 'bg-zinc-700/50 text-zinc-300',
};

const STATUS_ICONS: Record<TelemetryHealthStatus, typeof CheckCircle2> = {
  healthy: CheckCircle2,
  stale: Clock,
  no_recent_data: WifiOff,
  never_reported: XCircle,
  provider_error: AlertTriangle,
  zero_data_only: Wifi,
  unknown: HelpCircle,
};

// ─── Filter type ────────────────────────────────────────────────────────

type HealthFilter = 'all' | TelemetryHealthStatus | 'has_error' | 'report_blocked';

// ─── Detail panel ───────────────────────────────────────────────────────

function TelemetryDetailPanel({
  row,
  onClose,
}: {
  row: TelemetryHealthRow;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-y-0 right-0 w-full max-w-lg bg-wt-bg-primary border-l border-wt-border shadow-xl z-50 overflow-y-auto">
      <div className="p-5 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-sm font-semibold text-wt-text-primary">{row.property_name}</h2>
            <p className="text-xs text-wt-text-muted mt-0.5 font-mono">
              {row.provider_type} &middot; {row.org_name ?? 'No org'}
            </p>
          </div>
          <button onClick={onClose} className="text-wt-text-muted hover:text-wt-text-primary text-lg leading-none">
            &times;
          </button>
        </div>

        {/* Health status banner */}
        <div className={`rounded-lg p-3 text-xs ${row.health_status === 'healthy' ? 'bg-emerald-900/20 border border-emerald-800/40' : 'bg-amber-900/20 border border-amber-800/40'}`}>
          <div className="flex items-center gap-2">
            <StatusBadge status={row.health_status} />
          </div>
          <p className="mt-2 text-wt-text-secondary">{row.recommended_action}</p>
        </div>

        {/* Identity */}
        <Section title="Identity">
          <DetailRow label="Property ID" value={row.property_id.slice(0, 8) + '...'} />
          {row.system_id && <DetailRow label="System ID" value={row.system_id.slice(0, 8) + '...'} />}
          {row.plant_id && <DetailRow label="Plant ID" value={row.plant_id.slice(0, 8) + '...'} />}
          <DetailRow label="Provider" value={row.provider_type} />
          <DetailRow label="Organization" value={row.org_name ?? '—'} />
        </Section>

        {/* Polling status */}
        <Section title="Polling Status">
          <DetailRow label="Last poll" value={formatTimestamp(row.last_poll_at)} />
          <DetailRow
            label="Poll status"
            value={row.last_poll_status ?? 'Never polled'}
            warn={row.last_poll_status?.startsWith('error') ?? false}
          />
          <DetailRow label="Last provider OK" value={formatTimestamp(row.last_provider_ok_at)} />
          <DetailRow label="Last non-empty data" value={formatTimestamp(row.last_non_empty_telemetry_at)} />
          {row.last_provider_auth_failed_at && (
            <DetailRow label="Last auth failure" value={formatTimestamp(row.last_provider_auth_failed_at)} warn />
          )}
          <DetailRow
            label="Consecutive failures"
            value={String(row.consecutive_failures)}
            warn={row.consecutive_failures >= 3}
          />
          {row.backoff_until && (
            <DetailRow label="Backoff until" value={formatTimestamp(row.backoff_until)} warn />
          )}
        </Section>

        {/* Telemetry timeline */}
        <Section title="Telemetry Timeline">
          <DetailRow label="First daily summary" value={row.first_summary_date ?? 'None'} />
          <DetailRow label="Latest daily summary" value={row.latest_summary_date ?? 'None'} />
          <DetailRow label="Total daily summaries" value={String(row.total_daily_summaries)} />
          <DetailRow
            label="Days with data (30d)"
            value={String(row.days_with_data_30)}
            warn={row.days_with_data_30 < 7}
          />
          <DetailRow label="Days with data (60d)" value={String(row.days_with_data_60)} />
          <DetailRow
            label="All summaries zero"
            value={row.all_summaries_zero ? 'Yes' : 'No'}
            warn={row.all_summaries_zero && row.total_daily_summaries > 0}
          />
        </Section>

        {/* Report status */}
        <Section title="Report Status">
          <DetailRow label="Has subscription" value={row.has_report_subscription ? 'Yes' : 'No'} />
          {row.has_report_subscription && (
            <DetailRow label="Reports enabled" value={row.report_enabled ? 'Yes' : 'No'} warn={!row.report_enabled} />
          )}
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-wt-bg-secondary rounded-lg p-3 space-y-1.5 text-xs">
      <h3 className="text-[11px] text-wt-text-muted uppercase tracking-wider font-medium">{title}</h3>
      {children}
    </div>
  );
}

function DetailRow({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <div className="flex justify-between">
      <span className="text-wt-text-muted">{label}</span>
      <span className={`font-mono text-right max-w-[60%] truncate ${warn ? 'text-amber-400' : 'text-wt-text-primary'}`}>
        {value}
      </span>
    </div>
  );
}

// ─── Main tab ───────────────────────────────────────────────────────────

export function TelemetryHealthTab({ data, isLoading, error }: TelemetryHealthTabProps) {
  const [filter, setFilter] = useState<HealthFilter>('all');
  const [search, setSearch] = useState('');
  const [selectedRow, setSelectedRow] = useState<TelemetryHealthRow | null>(null);

  const filtered = useMemo(() => {
    if (!data) return [];
    let list = data.rows;
    switch (filter) {
      case 'all': break;
      case 'has_error':
        list = list.filter((r) => r.last_poll_status?.startsWith('error'));
        break;
      case 'report_blocked':
        list = list.filter((r) => r.has_report_subscription && r.health_status !== 'healthy');
        break;
      default:
        list = list.filter((r) => r.health_status === filter);
        break;
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((r) =>
        r.property_name.toLowerCase().includes(q) ||
        r.provider_type.toLowerCase().includes(q) ||
        (r.org_name?.toLowerCase().includes(q) ?? false)
      );
    }
    return list;
  }, [data, filter, search]);

  return (
    <AdminTabShell isLoading={isLoading} error={error}>
      {data && (
        <div className="space-y-4">
          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <SummaryCard label="Healthy" count={data.summary.healthy} className="text-emerald-400" />
            <SummaryCard label="Stale / No Data" count={data.summary.stale + data.summary.no_recent_data} className="text-amber-400" />
            <SummaryCard label="Provider Errors" count={data.summary.provider_error} className="text-red-400" />
            <SummaryCard label="Never Reported" count={data.summary.never_reported} className="text-red-400" />
            <SummaryCard label="Zero Data Only" count={data.summary.zero_data_only} className="text-purple-400" />
            <SummaryCard label="Total Monitored" count={data.summary.total} className="text-wt-text-primary" />
          </div>

          {/* Filters + search */}
          <div className="flex flex-wrap items-center gap-2">
            <FilterPill label="All" value="all" current={filter} onSelect={setFilter} />
            <FilterPill label="Healthy" value="healthy" current={filter} onSelect={setFilter} />
            <FilterPill label="Stale" value="stale" current={filter} onSelect={setFilter} />
            <FilterPill label="No Recent Data" value="no_recent_data" current={filter} onSelect={setFilter} />
            <FilterPill label="Never Reported" value="never_reported" current={filter} onSelect={setFilter} />
            <FilterPill label="Provider Error" value="provider_error" current={filter} onSelect={setFilter} />
            <FilterPill label="Zero Data" value="zero_data_only" current={filter} onSelect={setFilter} />
            <FilterPill label="Has Error" value="has_error" current={filter} onSelect={setFilter} />
            <FilterPill label="Report Blocked" value="report_blocked" current={filter} onSelect={setFilter} />
            <div className="ml-auto relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-wt-text-muted" />
              <Input
                placeholder="Search property/provider/org..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-7 pl-7 w-56 text-xs bg-wt-bg-secondary border-wt-border"
              />
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-wt-text-muted text-left border-b border-wt-border">
                  <th className="pb-2 pr-3 font-medium">Property</th>
                  <th className="pb-2 pr-3 font-medium">Org</th>
                  <th className="pb-2 pr-3 font-medium">Provider</th>
                  <th className="pb-2 pr-3 font-medium">Health</th>
                  <th className="pb-2 pr-3 font-medium text-right">Last Telemetry</th>
                  <th className="pb-2 pr-3 font-medium text-right">Days (30d)</th>
                  <th className="pb-2 pr-3 font-medium">Last Error</th>
                  <th className="pb-2 pr-3 font-medium">Report</th>
                  <th className="pb-2 pr-3 font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <tr
                    key={row.property_id}
                    className="border-b border-wt-border hover:bg-wt-bg-secondary/50 cursor-pointer transition-colors"
                    onClick={() => setSelectedRow(row)}
                  >
                    <td className="py-2 pr-3">
                      <span className="text-wt-text-primary font-medium truncate max-w-[180px] block">
                        {row.property_name}
                      </span>
                    </td>
                    <td className="py-2 pr-3 text-wt-text-secondary truncate max-w-[120px]">
                      {row.org_name ?? '—'}
                    </td>
                    <td className="py-2 pr-3 text-wt-text-secondary font-mono">
                      {row.provider_type}
                    </td>
                    <td className="py-2 pr-3">
                      <StatusBadge status={row.health_status} />
                    </td>
                    <td className="py-2 pr-3 text-right font-mono text-wt-text-secondary">
                      {formatRelativeTime(row.last_non_empty_telemetry_at ?? row.last_provider_ok_at)}
                    </td>
                    <td className="py-2 pr-3 text-right font-mono text-wt-text-primary">
                      {row.days_with_data_30}
                    </td>
                    <td className="py-2 pr-3 text-wt-text-muted truncate max-w-[150px]">
                      {row.last_poll_status?.startsWith('error')
                        ? row.last_poll_status.slice(0, 40)
                        : '—'}
                    </td>
                    <td className="py-2 pr-3">
                      {row.has_report_subscription ? (
                        <span className={`text-[10px] ${row.report_enabled ? 'text-emerald-400' : 'text-zinc-500'}`}>
                          {row.report_enabled ? 'Active' : 'Disabled'}
                        </span>
                      ) : (
                        <span className="text-zinc-600 text-[10px]">None</span>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-wt-text-muted text-[10px] max-w-[150px] truncate">
                      {row.health_status === 'healthy' ? '—' : row.recommended_action.slice(0, 50)}
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={9} className="py-8 text-center text-wt-text-muted">
                      No properties match the current filter.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Detail panel */}
      {selectedRow && (
        <TelemetryDetailPanel row={selectedRow} onClose={() => setSelectedRow(null)} />
      )}
    </AdminTabShell>
  );
}

// ─── Small components ───────────────────────────────────────────────────

function SummaryCard({ label, count, className }: { label: string; count: number; className?: string }) {
  return (
    <div className="bg-wt-bg-secondary rounded-lg p-3 border border-wt-border">
      <p className="text-[11px] text-wt-text-muted uppercase tracking-wider">{label}</p>
      <p className={`text-2xl font-mono font-semibold mt-1 ${className ?? 'text-wt-text-primary'}`}>
        {count}
      </p>
    </div>
  );
}

function FilterPill<T extends string>({
  label, value, current, onSelect,
}: { label: string; value: T; current: T; onSelect: (v: T) => void }) {
  const active = value === current;
  return (
    <button
      onClick={() => onSelect(value)}
      className={`px-2 py-0.5 rounded text-[11px] transition-colors ${
        active
          ? 'bg-wt-accent/20 text-wt-accent border border-wt-accent/40'
          : 'bg-wt-bg-tertiary text-wt-text-muted border border-transparent hover:border-wt-border'
      }`}
    >
      {label}
    </button>
  );
}

function StatusBadge({ status }: { status: TelemetryHealthStatus }) {
  const Icon = STATUS_ICONS[status];
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${STATUS_COLORS[status]}`}>
      <Icon className="h-2.5 w-2.5" />
      {STATUS_LABELS[status]}
    </span>
  );
}

function formatRelativeTime(iso: string | null): string {
  if (!iso) return 'Never';
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'Just now';
  if (min < 60) return `${min}m ago`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function formatTimestamp(iso: string | null): string {
  if (!iso) return 'Never';
  return new Date(iso).toLocaleString();
}
